/**
 * Landing screen (docs/06-UI-SPEC.md §2).
 *
 * Two cards of equal visual weight — neither is the default, neither is styled as the
 * primary action. No navigation, no marketing copy, no footer.
 */

import type { ReactNode } from 'react';

import { StudentIcon, TeacherIcon } from '../assets/icons.js';
import { Logo } from '../assets/Logo.js';
import { navigate, type Route } from '../router.js';

interface ChoiceCardProps {
  readonly icon: ReactNode;
  readonly label: string;
  readonly destination: Route;
}

/** One of the two entry cards. Both cards use exactly these styles. */
function ChoiceCard({ icon, label, destination }: ChoiceCardProps) {
  return (
    <button
      type="button"
      onClick={() => {
        navigate(destination);
      }}
      className="
        flex h-[200px] w-[280px] max-w-full flex-col items-center justify-center gap-6
        rounded-xl border border-border bg-surface text-text-primary shadow-sm
        transition duration-fast ease-out
        hover:-translate-y-[2px] hover:shadow-md
        active:translate-y-0
        max-[640px]:w-full
      "
    >
      <span className="text-text-secondary">{icon}</span>
      <span className="text-lg font-semibold">{label}</span>
    </button>
  );
}

export function LandingPage() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-16 bg-surface-sunken p-6">
      <Logo size={56} />

      <div className="flex w-full max-w-[640px] flex-wrap items-center justify-center gap-6">
        <ChoiceCard icon={<TeacherIcon size={40} />} label="I'm a Teacher" destination="/teacher" />
        <ChoiceCard icon={<StudentIcon size={40} />} label="I'm a Student" destination="/join" />
      </div>
    </main>
  );
}
